import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import jobService from '../../services/jobService';
import './ApplicationJobList.css';

const ApplicationsJobsList = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchJobs();
  }, [filter]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const params = {
        status: filter !== 'all' ? filter : undefined,
        limit: 50
      };

      const response = await jobService.getMyJobs(params);
      setJobs(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to fetch jobs');
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'badge-success',
      on_hold: 'badge-warning',
      closed: 'badge-danger',
      draft: 'badge-secondary'
    };
    return badges[status] || 'badge-secondary';
  };

  const getStatusIcon = (status) => {
    const icons = {
      active: 'check_circle',
      on_hold: 'pause_circle',
      closed: 'cancel',
      draft: 'draft'
    };
    return icons[status] || 'help';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getApplicationCount = (job) => {
    return job.applications || 0;
  };

  const statusConfig = {
    active:   { label: 'Active',   color: 'green',  icon: 'check_circle' },
    on_hold:  { label: 'On Hold',  color: 'orange', icon: 'pause_circle' },
    closed:   { label: 'Closed',   color: 'red',    icon: 'cancel' },
    draft:    { label: 'Draft',    color: 'gray',   icon: 'draft' },
  };

  const getCfg = (s) => statusConfig[s] || { label: s, color: 'gray', icon: 'work' };

  const tabs = [
    { key: 'all',     label: 'All',     count: jobs.length },
    { key: 'active',  label: 'Active',  dot: 'green' },
    { key: 'on_hold', label: 'On Hold', dot: 'orange' },
    { key: 'closed',  label: 'Closed',  dot: 'red' },
    { key: 'draft',   label: 'Draft',   dot: 'gray' },
  ];

  if (loading) {
    return (
      <div className="emp-app-job-list ajl-container">
        <div className="ajl-loading"><div className="ajl-spinner"></div><p>Loading jobs…</p></div>
      </div>
    );
  }

  return (
    <div className="emp-app-job-list ajl-container">
      {/* Header */}
      <div className="ajl-header">
        <div>
          <h1 className="ajl-title">Applications</h1>
          <p className="ajl-subtitle">Select a job to review its candidate applications</p>
        </div>
        <Link to="/employer/post-job" className="ajl-cta-btn">
          <span className="material-symbols-outlined">add</span>Post New Job
        </Link>
      </div>

      {/* How it works banner */}
      <div className="ajl-flow-banner">
        {[
          { icon: 'work', label: 'Post a Job' },
          { icon: 'arrow_forward', label: '' },
          { icon: 'people', label: 'Candidates Apply' },
          { icon: 'arrow_forward', label: '' },
          { icon: 'visibility', label: 'Review Here' },
          { icon: 'arrow_forward', label: '' },
          { icon: 'star', label: 'Shortlist' },
          { icon: 'arrow_forward', label: '' },
          { icon: 'video_camera_front', label: 'Interview' },
          { icon: 'arrow_forward', label: '' },
          { icon: 'check_circle', label: 'Hire' },
        ].map((s, i) => s.label
          ? <span key={i} className="ajl-flow-step">
              <span className="material-symbols-outlined">{s.icon}</span>
              {s.label}
            </span>
          : <span key={i} className="material-symbols-outlined ajl-flow-arrow">{s.icon}</span>
        )}
      </div>

      {/* Tabs */}
      <div className="ajl-tabs-wrap">
        <div className="ajl-tabs">
          {tabs.map(t => (
            <button
              key={t.key}
              className={`ajl-tab ${filter === t.key ? 'active' : ''}`}
              onClick={() => setFilter(t.key)}
            >
              {t.dot && <span className={`ajl-tab-dot ${t.dot}`}></span>}
              {t.label}
              {t.count !== undefined && <span className="ajl-tab-count">{t.count}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Jobs */}
      {jobs.length === 0 ? (
        <div className="ajl-empty">
          <span className="material-symbols-outlined">work_off</span>
          <h3>No jobs found</h3>
          <p>{filter === 'all' ? "Post your first job to start receiving applications" : `No ${filter.replace('_',' ')} jobs found`}</p>
          {filter === 'all' && (
            <Link to="/employer/post-job" className="ajl-cta-btn">
              <span className="material-symbols-outlined">add</span>Post Your First Job
            </Link>
          )}
        </div>
      ) : (
        <div className="ajl-list">
          {jobs.map((job) => {
            const cfg = getCfg(job.status);
            const appCount = getApplicationCount(job);
            return (
              <div key={job._id} className="ajl-card">
                <div className="ajl-card-left">
                  <div className="ajl-card-icon">
                    <span className="material-symbols-outlined">work</span>
                  </div>
                  <div className="ajl-card-body">
                    <div className="ajl-card-title-row">
                      <h3>{job.title}</h3>
                      <span className={`ajl-badge ${cfg.color}`}>
                        <span className="material-symbols-outlined">{cfg.icon}</span>
                        {cfg.label}
                      </span>
                    </div>
                    <div className="ajl-card-meta">
                      <span><span className="material-symbols-outlined">business</span>{job.company?.name || 'Company'}</span>
                      <span><span className="material-symbols-outlined">location_on</span>{job.location?.city?.name || job.location?.city || 'Remote'}</span>
                      <span><span className="material-symbols-outlined">calendar_today</span>{formatDate(job.createdAt)}</span>
                      {job.jobType?.name && <span><span className="material-symbols-outlined">business_center</span>{job.jobType.name}</span>}
                      {job.salary?.min && <span><span className="material-symbols-outlined">currency_rupee</span>{job.salary.min}–{job.salary.max} {job.salary.currency}</span>}
                    </div>
                    {job.description && (
                      <p className="ajl-card-desc">{job.description.length > 120 ? `${job.description.substring(0, 120)}…` : job.description}</p>
                    )}
                  </div>
                </div>
                <div className="ajl-card-right">
                  <div className="ajl-app-count">
                    <span className="ajl-app-num">{appCount}</span>
                    <span className="ajl-app-label">Applicant{appCount !== 1 ? 's' : ''}</span>
                  </div>
                  <Link to={`/employer/jobs/${job._id}/applications`} className="cst-deafult-btn">
                    <span className="material-symbols-outlined">arrow_forward</span>
                    View Applications
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ApplicationsJobsList;
